import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _without from 'lodash/without';
import { useSessionStorage } from 'usehooks-ts';
import {
  Access,
  AccessComponentEnum,
  AccessLevelEnum,
  UserRoleEnum,
} from 'graphql-common';
import { ToastContainer } from '@lib/components/Toast/Toast';
import SessionExpiredModal from '@lib/components/SessionExpiredModal';
import LogoLoader from '@lib/components/LogoLoader/LogoLoader';
import {
  GetUrlParams,
  PATH_AUTH,
  PATH_COMPANY_DOMAINS,
  PATH_COMPANY_PLAN,
  PATH_ERROR,
  PATH_INTERVENTIONS,
  PATH_REMINDER,
  PATH_SSO,
} from '@lib/enums/urls';
import generateSafeRedirect from '@lib/utils/generateSafeRedirect';
import { setSentryTags } from '@lib/utils/sentryHelpers';
import UpdateHtmlLang from '@lib/components/UpdateHtmlLang/UpdateHtmlLang';
import {
  checkCompanyPlanAccessByComponent,
  checkUserAccessByComponent,
  checkUserAccessForPage,
} from '@lib/utils/accesses';
import useWhoamiQueryHook from 'utils/fetch/hooks/useWhoamiQueryHook';
import WhoamiContext from 'hooks/useWhoamiContext';
import { APP_URLS } from 'constants/urls';
import getHomePageUrl from 'layouts/DashboardLayout/utils/getHomePageUrl';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import { ANIMATION_LOADER_AVAILABLE } from '@lib/enums/localStorageKeys';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import useTechnicalPause from '@lib/hooks/useTechnicalPause';
import { useTabActivity } from '@lib/hooks/useTabActivity';
import TechnicalPauseContext from '@lib/contexts/TechnicalPauseContext';
import TechnicalPauseTracker from '@lib/components/TechnicalPauseTracker/TechnicalPauseTracker';
import ReleaseTrackerProvider from '@lib/components/ReleaseTracker/ReleaseTrackerProvider';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';

const { MODE, VITE_AUTH_PLATFORM_URL: AUTH_PLATFORM } = import.meta.env;

function ProtectedRouteContent() {
  const { pathname, search } = useLocation();
  const { i18n } = useTranslation();
  const urlSearchParams = new URLSearchParams(search);
  const dst = urlSearchParams.get(GetUrlParams.Dst) as string;
  const isDst = dst && dst !== '/';
  const pathnameArr = _without(pathname.split('/'), '');
  const pathBase = pathnameArr.length ? pathnameArr[0] : '/';

  const [showLogo, setShowLogo] = useSessionStorage(
    ANIMATION_LOADER_AVAILABLE,
    true,
  );
  const shouldShowLogo = showLogo && pathBase !== PATH_AUTH && !isDst;

  const technicalPauseResult = useTechnicalPause();
  const { activeTechnicalPause, fetched: technicalPauseFetched } =
    technicalPauseResult;

  // Whoami — the single query instance for the whole app. Everything under
  // this route (DashboardLayout included) must read it from WhoamiContext
  // rather than calling useWhoamiQueryHook again: a second independent
  // `cache-only` subscription races with this one's fetches and can
  // permanently miss a cache broadcast. See DAT-1551.
  const whoamiResult = useWhoamiQueryHook();
  const { data, loading, error, currentUserLang, firstLoading, refetch } =
    whoamiResult;
  // `whoami` (and the company plan accesses it carries) is only fetched once
  // per app load — a plan change made in another tab/session (e.g. SystemAdmin
  // disabling a feature) otherwise stays invisible here — and everywhere that
  // reads it via cache-only, like the sidebar nav lock state — until a hard
  // refresh. Refetch when the tab regains focus so it catches up on its own.
  useTabActivity({ onFocus: () => refetch() });
  const userId = _get(data, 'whoami.resource.id');
  const userEmail = _get(data, 'whoami.resource.email');
  const username = _get(data, 'whoami.resource.username');
  const deactivatedAt = _get(data, 'whoami.resource.deactivatedAt');
  const userType = _get(data, 'whoami.resource.__typename');
  const passwordSetupRequired = _get(
    data,
    'whoami.resource.passwordSetupRequired',
  );
  const userRole = _get(data, 'whoami.resource.role');
  const isFullAdmin = userRole === UserRoleEnum.FullAdmin;
  const finalAccesses = _get(data, 'whoami.resource.finalAccesses', []);
  const companyAccesses = _get(
    data,
    'whoami.resource.company.plan.accesses',
    [],
  ) as Access[];
  const graphQLErrors = getGraphQLErrors(error);
  const authError = graphQLErrors.find(
    ({ extensions }) => extensions?.code === 'AUTHENTICATION_ERROR',
  );
  const isUserAuth = !authError && !!userId;
  const isUserDeactivated = !!deactivatedAt;
  // Once whoami has resolved successfully at least once (e.g. `userId` is
  // cached), a *background* refetch failure — like the tab-focus refetch
  // going out while offline — must not evict an already-authenticated user.
  // Only a failure with no cached session yet is fatal.
  const hasCachedSession = !!userId;
  const loginUrl = `${AUTH_PLATFORM}/${PATH_AUTH}/login`;

  const userAccessForPage = checkUserAccessForPage(finalAccesses, pathname);
  // Plan gating is feature-level, not page-level: sub-pages like
  // /interventions/categories map to components a plan payload never
  // enumerates, so check the gated component directly instead of going
  // through the pathname→component map.
  const isInterventionsPlanLocked =
    checkCompanyPlanAccessByComponent(
      companyAccesses,
      AccessComponentEnum.InterventionsManagement,
    ) === AccessLevelEnum.LimitedRead;
  const defaultUrlAfterAuth = getHomePageUrl(finalAccesses);

  if (MODE !== 'development' && !loading) {
    setSentryTags({ isUserAuth, userId, userEmail, userType, username });
  }

  const appRender = () => (
    <TechnicalPauseContext.Provider value={technicalPauseResult}>
      <WhoamiContext.Provider value={whoamiResult}>
        <QueryParamProvider adapter={ReactRouter6Adapter}>
          <UpdateHtmlLang />
          <Outlet />
          <ToastContainer />
          <SessionExpiredModal loginUrl={loginUrl} />
        </QueryParamProvider>
      </WhoamiContext.Provider>
    </TechnicalPauseContext.Provider>
  );

  const appNavigate = (to: string, external?: boolean, dstUrl?: string) => {
    if (!external) return <Navigate to={to} replace />;
    const toUrlSearch =
      dstUrl && dstUrl !== '/' ? `?${GetUrlParams.Dst}=${dstUrl}` : '';
    window.location.href = `${to}${toUrlSearch}`;
    return undefined;
  };

  useEffect(() => {
    if (i18n.language !== currentUserLang) {
      i18n.changeLanguage(currentUserLang);
    }
  }, [i18n, currentUserLang]);

  useEffect(() => {
    if (firstLoading && shouldShowLogo) {
      const timer = setTimeout(() => {
        setShowLogo(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [firstLoading, setShowLogo, shouldShowLogo]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(ANIMATION_LOADER_AVAILABLE, 'true');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [setShowLogo]);

  if (shouldShowLogo) {
    return <LogoLoader setVisible={setShowLogo} />;
  }
  if (firstLoading) {
    return <CircleLoader />;
  }
  // Wait for CDN check before showing error — avoids race condition
  if (error && !authError && !technicalPauseFetched && !hasCachedSession) {
    return <CircleLoader />;
  }
  if (activeTechnicalPause) {
    return (
      <TechnicalPauseTracker
        technicalPauseEndDate={activeTechnicalPause.endAt}
      />
    );
  }
  if (pathBase === PATH_AUTH) {
    if (dst && dst !== '/') {
      window.location.href = dst;
      return undefined;
    }
    return appNavigate(APP_URLS.dashboard.index.path);
  }
  if (pathBase === PATH_ERROR && !error) {
    return appNavigate(APP_URLS.dashboard.index.path);
  }
  if (pathBase === PATH_ERROR && authError) {
    appNavigate(loginUrl, true);
    return undefined;
  }
  if (pathBase === PATH_ERROR && error) {
    return (
      <ErrorPage
        status={getStatusFromError(error)}
        description={getDescriptionFromError(error)}
        logoUrl={APP_URLS.dashboard.index.path}
        fullHeight
      />
    );
  }
  if (error && !authError && !hasCachedSession) {
    return appNavigate(APP_URLS.error.path);
  }
  if (!isUserAuth) {
    const dstUrl = generateSafeRedirect(pathname + search);
    appNavigate(loginUrl, true, dstUrl.toString());
    return undefined;
  }
  if (passwordSetupRequired) {
    const dstUrl = generateSafeRedirect(pathname + search);
    appNavigate(loginUrl, true, dstUrl.toString());
    return undefined;
  }
  if (isUserDeactivated) {
    if (pathname !== APP_URLS.dashboard.unauthorized.path) {
      return appNavigate(APP_URLS.dashboard.unauthorized.path);
    }
  }
  if (!isUserDeactivated && pathname === APP_URLS.dashboard.unauthorized.path) {
    return appNavigate(APP_URLS.dashboard.notFound.path);
  }
  if (
    isInterventionsPlanLocked &&
    pathname.includes(`/${PATH_INTERVENTIONS}`) &&
    !APP_URLS.dashboard.interventions.index.isTheSameUrlAs(pathname)
  ) {
    return appNavigate(APP_URLS.dashboard.interventions.index.path);
  }
  // Documentation is gated as a feature; the linked-documents tab on a form
  // template uses gated filter args + queries that fail for LimitedRead users.
  if (
    APP_URLS.dashboard.formTemplates.viewLinkedDocuments.isTheSameUrlAs(
      pathname,
    ) &&
    checkUserAccessByComponent(
      companyAccesses,
      AccessComponentEnum.DocumentationManagement,
    ) === AccessLevelEnum.LimitedRead
  ) {
    return appNavigate(APP_URLS.dashboard.accessDenied.path);
  }
  // Asset's linked-documents tab and its nested document view are accessible
  // on the free plan — asset documents are the one entity allowed under
  // company-level EDM lock (per EDM accessibility scopes). Only a user-role
  // restriction (LimitedRead on finalAccesses) gates the route.
  if (
    /^\/assets\/[^/]+\/documentation(?:\/[^/]+)?$/.test(pathname) &&
    checkUserAccessByComponent(
      finalAccesses,
      AccessComponentEnum.DocumentationManagement,
    ) === AccessLevelEnum.LimitedRead
  ) {
    return appNavigate(APP_URLS.dashboard.accessDenied.path);
  }
  // EDM premium sub-routes (Analytics / Approvals / Categories) only render a
  // FeatureLockState upgrade affordance when the company plan locks
  // DocumentationManagement — that's a Full-Admin-only CTA. Non-Full-Admin
  // users can't unlock, so deny them the page entirely. Company-level access
  // wins over user-role access here.
  if (
    !isFullAdmin &&
    checkUserAccessByComponent(
      companyAccesses,
      AccessComponentEnum.DocumentationManagement,
    ) === AccessLevelEnum.LimitedRead &&
    (APP_URLS.dashboard.documentation.analytics.isTheSameUrlAs(pathname) ||
      APP_URLS.dashboard.documentation.approvals.isTheSameUrlAs(pathname) ||
      APP_URLS.dashboard.documentation.categories.isTheSameUrlAs(pathname))
  ) {
    return appNavigate(APP_URLS.dashboard.accessDenied.path);
  }
  if (
    !isFullAdmin &&
    (pathname.startsWith(`/${PATH_SSO}`) ||
      pathname.startsWith(`/${PATH_COMPANY_DOMAINS}`) ||
      pathname.startsWith(`/${PATH_COMPANY_PLAN}`) ||
      pathname.startsWith(`/${PATH_REMINDER}`))
  ) {
    return appNavigate(APP_URLS.dashboard.accessDenied.path);
  }
  if (userAccessForPage === AccessLevelEnum.LimitedRead) {
    return appNavigate(APP_URLS.dashboard.accessDenied.path);
  }
  if (isUserAuth && !pathnameArr.length) {
    return appNavigate(defaultUrlAfterAuth);
  }
  return appRender();
}

function ProtectedRoute() {
  return (
    <ReleaseTrackerProvider>
      <ProtectedRouteContent />
    </ReleaseTrackerProvider>
  );
}

export default ProtectedRoute;
